/**
 * ModelIntake styles — canvas 02, states A→D.
 * Spec: thinking/design/handoffs/training_shift/Model Intake.html + canvas.css.
 * Tokens: narcis/CLAUDE.md design-system section (colors.scss globals, no @use needed).
 */

.intake {
  max-width: 1120px;
  margin: 0 auto;
  padding: 40px 16px 88px;
  width: 100%;

  @media (min-width: 600px) {
    padding: 48px 24px 88px;
  }
}

.eyebrow {
  font-family: var(--font-mono);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 11px;
  color: var(--yolk);
  margin: 0 0 10px;
}

.h1 {
  font-family: var(--font-display);
  font-style: italic;
  font-weight: 500;
  font-size: 40px;
  line-height: 1.1;
  color: var(--text);
  margin: 0 0 14px;

  @media (min-width: 900px) {
    font-size: 46px;
  }
}

.sub {
  color: var(--text-muted);
  font-size: 16px;
  max-width: 58ch;
  margin: 0 0 40px;
}

// The price is said under the title, before the effort: the sub line gives way to it.
.subAbovePrice {
  margin-bottom: 10px;
}

.priceLine {
  color: var(--text);
  font-size: 14.5px;
  max-width: 58ch;
  margin: 0 0 40px;
}

// ── DB-state head note (ready/failed) ────────────────────────────────────────

.modelNote {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: 14px 18px;
  margin: -16px 0 32px;
  font-size: 13.5px;
  color: var(--text-muted);
  line-height: 1.55;
}

.modelNoteDismiss {
  flex-shrink: 0;
  background: none;
  border: none;
  color: var(--text-dim);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--text-muted);
  }
}

// ── Guidance row ─────────────────────────────────────────────────────────────

.guide {
  display: flex;
  gap: 32px;
  margin-bottom: 44px;
  flex-wrap: wrap;
}

.guideCol {
  flex: 1;
  min-width: 260px;
}

.guideLabel {
  font-family: var(--font-mono);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 11px;
  color: var(--text-dim);
  margin: 0 0 12px;
}

.guideTiles {
  display: flex;
  gap: 12px;
}

.guideTile {
  flex: 1;
  min-width: 0;
}

.guideImg {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: var(--radius-md);
  background: var(--surface);
  border: 1px solid var(--border);
}

.guideCap {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
  font-size: 12.5px;
  color: var(--text-muted);
}

.ok {
  color: var(--success);
  font-size: 12px;
}

.no {
  color: var(--text-dim);
  font-size: 12px;
}

// ── The panel: one ambient tone for its top rule, the state line and the ledger ──────────────────
// Collecting / working / worth knowing / needs you / ready (canvas "state key", 2026-10-08).

.panel {
  --tone: var(--text-muted);
  --tone-rule: var(--border-med);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  overflow: hidden;
}

.toneCollecting {
  --tone: var(--text-muted);
  --tone-rule: var(--border-med);
}

.toneWorking {
  --tone: var(--yolk);
  --tone-rule: var(--yolk);
}

.toneWorth {
  --tone: var(--caution);
  --tone-rule: var(--caution);
}

.toneNeeds {
  --tone: var(--error);
  --tone-rule: var(--error);
}

.toneReady {
  --tone: var(--success);
  --tone-rule: var(--success);
}

.rule {
  height: 3px;
  background: var(--tone-rule);
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--tone);
  flex: none;
}

// ── Bench (grid + drop + rail) ─────────────────────────────────────────────────

.bench {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: start;
  padding: 16px 14px 20px;

  @media (min-width: 600px) {
    padding: 20px 18px 24px;
  }

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1fr) 340px;
    gap: 40px;
    padding: 32px 36px 36px;
  }
}

.benchMain {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
}

.drop {
  border: 1px dashed var(--border-med);
  border-radius: var(--radius-lg);
  padding: 20px;
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
  cursor: pointer;

  b {
    color: var(--text);
    font-weight: 600;
  }
}

// A finger taps, a pointer clicks: the words follow the device, in CSS so the markup is the same on both.
.dropTouch {
  display: none;
}

@media (hover: none) and (pointer: coarse) {
  .dropMouse {
    display: none;
  }

  .dropTouch {
    display: inline;
  }
}

// ── Slot grid ───────────────────────────────────────────────────────────────

// Phones and tablets count columns (3, then 4); beside the 340px rail the tile has a floor and the
// column count follows the room — a fixed count there shrank tiles to ~60px on a laptop-width window.
.slots {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
    gap: 12px;
  }
}

.slot {
  position: relative;
  aspect-ratio: 3 / 4;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--surface-hover);
  border: 1px solid var(--border-med);
  container-type: inline-size;
}

.ph {
  position: absolute;
  inset: 0;
}

.slotImg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slotEmpty {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--border-med);
  background: none;
  cursor: pointer;
}

.slotPlus {
  font-size: 22px;
  color: var(--text-dim);
}

// Tile states. The fault sits on the photo, in full words: the caption wraps, it is never cut.
.cap {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 8px 9px;
  background: rgba(14, 14, 16, 0.82);
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 1.35;
  // A caption breaks between words, never inside one (Safari split "Almost" into "Alm / ost").
  overflow-wrap: normal;
  word-break: normal;
  hyphens: manual;

  b {
    display: block;
    font-weight: 500;
    font-size: 9.5px;
    letter-spacing: 0.08em;
    margin-bottom: 2px;
  }

  // A phone-width tile (under the 112px floor beside the rail): the same words in a smaller hand, so
  // the caption takes about a third of the photo.
  @container (max-width: 111px) {
    padding: 5px 6px;
    font-size: 9px;
    line-height: 1.25;

    b {
      display: inline;
      margin: 0 4px 0 0;
      font-size: 8.5px;
    }
  }
}

.tile_checking .cap {
  color: var(--yolk);
}

.tile_not-used,
.tile_worth,
.tile_gone {
  .cap {
    color: var(--text);
    text-transform: none;
    letter-spacing: 0;
    font-family: var(--font-body);
    font-size: 11.5px;
    background: rgba(14, 14, 16, 0.9);

    @container (max-width: 111px) {
      font-size: 10px;
    }
  }

  .ph,
  .slotImg {
    opacity: 0.55;
  }
}

.tile_not-used {
  border: 1.5px solid var(--error);

  .cap b {
    color: var(--error);
  }
}

.tile_worth {
  border: 1.5px solid var(--caution);

  // Still counted: the photo keeps its full light.
  .ph,
  .slotImg {
    opacity: 1;
  }

  .cap b {
    color: var(--caution);
  }
}

.tile_gone {
  border: 1.5px dashed var(--error);

  .cap b {
    color: var(--error);
  }
}

.veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(100deg, rgba(24, 24, 27, 0.55) 30%, rgba(31, 31, 35, 0.35) 50%, rgba(24, 24, 27, 0.55) 70%);
  background-size: 200% 100%;
  animation: shimmerSlide 2.2s linear infinite;
}

@keyframes shimmerSlide {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}

.slotX {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(14, 14, 16, 0.5);
  border: 0;
  color: rgba(250, 250, 249, 0.8);
  font-size: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  // The disc is small so the face stays clear; the finger still gets about 40px.
  &::before {
    content: '';
    position: absolute;
    inset: -10px;
  }

  &:hover {
    background: rgba(14, 14, 16, 0.8);
    color: var(--text);
  }
}

// ── Notice lines: events with no photo to sit on ───────────────────────────────

.notices {
  display: flex;
  flex-direction: column;
  gap: 8px;
  // The caution hue is this panel's own; the wall keeps `--warning` for its notices.
  --notice-warning: var(--caution);
}

.notices p,
.noticeLine {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 13.5px;
  line-height: 1.45;
  text-transform: none;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background: currentColor;
    flex: none;
  }
}

// The text keeps the page's colour; the dot carries the kind.
.notices p[data-kind] {
  color: var(--text);
}

.notices p[data-kind='info']::before {
  background: var(--notice-info);
}

.notices p[data-kind='success']::before {
  background: var(--notice-success);
}

.notices p[data-kind='warning']::before {
  background: var(--notice-warning);
}

.notices p[data-kind='error']::before {
  background: var(--notice-error);
}

.noticeRow {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.noticeDismiss {
  flex: none;
  background: none;
  border: none;
  padding: 2px 4px;
  color: var(--text-dim);
  font-size: 13px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: var(--text-muted);
  }
}

.noticeLine a {
  color: var(--yolk);
}

// ── Right rail: state line + meter + ledger + CTA ──────────────────────────────

.rail {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 900px) {
    position: sticky;
    top: 24px;
  }
}

.stateLine {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--tone);
}

// The rail's consent, price and Start, kept together so the phone pill can point at them as one.
.startArea {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// The way down to Start in the top state line: yolk, the interaction colour.
.continueLink {
  background: none;
  border: 0;
  padding: 6px 0;
  margin: -6px 0;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: var(--yolk);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: var(--yolk-hover);
  }
}

// Below 900px the state line is said above the tiles (`stateTop`); beside the rail it is the rail's.
.stateTop {
  @media (min-width: 900px) {
    display: none;
  }
}

.stateRail {
  @media (max-width: 899px) {
    display: none;
  }
}

.meter {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.meterCount {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-family: var(--font-display);
  font-size: 34px;
  line-height: 1.1;
  color: var(--text);
  margin: 0;

  small {
    font-size: 13px;
    color: var(--text-muted);
    font-family: var(--font-body);
  }
}

.meterBar {
  height: 6px;
  border-radius: var(--radius-full, 9999px);
  background: var(--surface-hover);
  overflow: hidden;

  i {
    display: block;
    height: 100%;
    border-radius: var(--radius-full, 9999px);
    background: var(--yolk);
    transition: width 0.3s ease;
  }
}

.meterBarFull i {
  background: var(--success);
}

.meterBody {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text);
}

.meterNote,
.railNote {
  font-size: 12.5px;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.5;
}

.ledger {
  list-style: none;
  margin: 0;
  padding: 4px 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ledgerRow {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13px;
  line-height: 1.45;

  .dot {
    margin-top: 6px;
  }
}

.ledgerText {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--text);
}

.ledgerSub {
  display: block;
  color: var(--text-muted);
}

.ledgerCount {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-muted);
}

// A refusal or news line in the rail, toned like the rest.
.railLine {
  display: flex;
  gap: 10px;
  margin: 0;
  font-size: 13.5px;
  line-height: 1.45;

  .dot {
    margin-top: 6px;
  }

  span:last-child {
    color: var(--text);
  }

  a {
    color: var(--yolk);
  }
}

.reference {
  font-family: var(--font-mono);
  font-size: 12px;
  word-break: break-all;
}

// Intake consent (funnel spec §8) — same recipe as TosConsentCard.module.scss's `.checkbox`
// (the pack page's pre-existing 18+/ToS gate), reused rather than re-derived so every consent
// checkbox on the site shares one visual language.
.consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 12.5px;
  color: var(--text-muted);
  line-height: 1.5;
  text-align: left;
  margin: 0;

  input[type='checkbox'] {
    margin-top: 3px;
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    accent-color: var(--yolk);
    cursor: pointer;
  }

  a {
    color: var(--yolk);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
}

// The consent appears on the first press of Start: a short, calm arrival.
.consentReveal {
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: consentIn 0.22s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
}

@keyframes consentIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
}

// Start was refused for the missing consent: the box is ringed.
.consentRing input[type='checkbox'] {
  outline: 2px solid var(--error);
  outline-offset: 2px;
}

.btnYolk,
.btnDisabled,
.btnGhost {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border: none;
  border-radius: var(--radius-full, 9999px);
  height: 48px;
  padding: 0 20px;
  font-size: 15px;
  font-weight: 600;
  font-family: var(--font-body);
  cursor: pointer;
  text-decoration: none;
}

.btnYolk {
  background: var(--yolk);
  color: var(--ink);

  &:hover {
    background: var(--yolk-hover);
  }
}

.btnBusy {
  cursor: progress;

  &:hover {
    background: var(--yolk);
  }
}

.spin {
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  border: 2px solid rgba(42, 42, 38, 0.35);
  border-top-color: var(--ink);
  flex: none;
  animation: spinTurn 0.8s linear infinite;
}

@keyframes spinTurn {
  to { transform: rotate(360deg); }
}

.btnDisabled {
  background: var(--surface-hover);
  color: var(--text-muted);
  cursor: not-allowed;
}

.btnGhost {
  background: none;
  border: 1px solid var(--border-med);
  color: var(--text);
}

.railFoot {
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.5;
  margin: 0;

  em {
    color: var(--text-muted);
    font-style: normal;
  }
}

// ── Sign-in in the rail (replaces the Start button once the box is ticked) ────

.signIn {
  display: flex;
  flex-direction: column;
  gap: 12px;
  // MagicLinkForm hooks: in the narrow rail the button drops under the field instead of squeezing it
  --auth-email-row-wrap: wrap;
  --auth-email-input-flex: 1 1 160px;
}
